/**
 * ImageUploadElement - Editor element for image upload fields
 *
 * Features:
 * - Checkbox for enable/disable state
 * - MediaUploadField for URL input and file upload
 * - Gear icon for style settings (when styleFields provided)
 * - Style settings panel (expandable)
 *
 * Pattern: Follows TextElement structure exactly
 *
 * Sprint: background-section
 * Phase: 2 - ImageUploadElement
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Checkbox,
  IconButton,
  Typography,
  Collapse,
  Tooltip,
  CircularProgress,
  Button,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { MediaUploadField, WithGuideHelp } from '@modules/ui-components';
import type { FieldConfig } from '../../types/editorFieldTypes';
import { useEditorGuideService } from '../../contexts';
import { useMultiLocaleUpload } from '../../hooks/useMultiLocaleUpload';
import { PaddingElement, type PaddingValues } from './PaddingElement';
import {
  mainFieldLabelEnabledSx,
  mainFieldLabelDisabledSx,
  checkboxIconSx,
  checkboxCheckedIconSx,
  checkboxSx,
  SHOW_FIELD_PATH_TOOLTIP,
  expandedSectionSx,
  expandedToggleButtonSx,
  expandedToggleButtonActiveSx,
} from '../../config/editorFieldStyles';
import {
  FIELD_MARGIN_BOTTOM,
  FIELD_GAP,
  FIELD_GAP_SMALL,
  FIELD_STYLE_GRID_GAP,
  FIELD_STYLE_MARGIN_TOP,
  FIELD_STYLE_PADDING_LEFT,
} from '../../config/ui';
import { StyleFieldRow } from '../../components/shared/styleFields';
import { getStyleInputTestId, getStyleCheckboxTestId } from '../../utils/testIdUtils';

/**
 * Props for ImageUploadElement component
 */
export interface ImageUploadElementProps {
  /** Field configuration */
  fieldConfig: FieldConfig;

  /** Current value (URL string or null) */
  value: string | null;

  /** Template value for restoration */
  templateValue: unknown;

  /** Whether field is enabled (checkbox state) */
  enabled: boolean;

  /** Current locale for l10n */
  locale: string;

  /** Callback when value changes */
  onChange: (value: string | null) => void;

  /** Callback when enabled state changes */
  onEnabledChange: (enabled: boolean) => void;

  /** Callback for file upload - should return URL */
  onUpload: (file: File) => Promise<string>;

  /** Whether entire element is disabled (save state) */
  disabled?: boolean;

  /** Hide the checkbox (when nullable is controlled externally) */
  hideCheckbox?: boolean;

  /** Whether style settings are expanded */
  expandedStyles?: boolean;

  /** Callback when style expansion toggles */
  onStylesExpandToggle?: () => void;

  /** Style values for nested style fields */
  styleValues?: Record<string, unknown>;

  /** Callback when style value changes */
  onStyleChange?: (path: string, value: unknown) => void;

  /** data-testid for the main field container */
  'data-testid'?: string;

  /** data-testid for the checkbox */
  checkboxTestId?: string;

  /** data-testid for the styles toggle button (gear icon) */
  stylesToggleTestId?: string;

  /** data-testid for the input field */
  inputTestId?: string;

  /** data-testid for the upload button icon */
  uploadButtonTestId?: string;

  /** data-testid for the preview box element */
  previewTestId?: string;
}

/**
 * ImageUploadElement - Editor element for image upload
 *
 * Layout:
 * [✓] Label
 * [URL/File input_____________________] [📷] [⚙]
 *
 * When gear is expanded:
 * [✓] Label
 * [URL/File input_____________________] [📷] [⚙]
 * ├── [✓] Scale Mode: [Select▼]
 * └── [✓] Other styles...
 */
export const ImageUploadElement: React.FC<ImageUploadElementProps> = ({
  fieldConfig,
  value,
  templateValue,
  enabled,
  locale,
  onChange,
  onEnabledChange,
  onUpload,
  disabled = false,
  hideCheckbox = false,
  expandedStyles: expandedStylesProp,
  onStylesExpandToggle,
  styleValues = {},
  onStyleChange,
  'data-testid': dataTestId,
  checkboxTestId,
  stylesToggleTestId,
  inputTestId,
  uploadButtonTestId,
  previewTestId,
}) => {
  // Internal state for style expansion (controlled externally or internally)
  const [internalExpanded, setInternalExpanded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const isStylesExpanded = expandedStylesProp ?? internalExpanded;

  const hasStyleFields = fieldConfig.styleFields && fieldConfig.styleFields.length > 0;
  const hasPaddingConfig = !!fieldConfig.paddingConfig;
  const hasExpandableContent = hasStyleFields || hasPaddingConfig;

  // Visual disabled state: when hideCheckbox=true, use disabled prop for visual styling
  // This ensures label looks disabled when the parent component is disabled
  const isVisuallyDisabled = disabled || !enabled;

  // Get guideService from context for WithGuideHelp
  const guideService = useEditorGuideService();

  // Get padding values from styleValues when paddingConfig exists
  const paddingValues: PaddingValues = useMemo(() => {
    if (!fieldConfig.paddingConfig) {
      return { paddingTop: 0, paddingRight: 0, paddingBottom: 0, paddingLeft: 0 };
    }
    const basePath = fieldConfig.paddingConfig.basePath;
    const usesEdgeInsets = basePath.endsWith('.layout.insets') || basePath.endsWith('.insets');

    if (usesEdgeInsets) {
      return {
        paddingTop: (styleValues[`${basePath}.top`] as number) ?? 0,
        paddingRight: (styleValues[`${basePath}.end`] as number) ?? 0,
        paddingBottom: (styleValues[`${basePath}.bottom`] as number) ?? 0,
        paddingLeft: (styleValues[`${basePath}.start`] as number) ?? 0,
      };
    }

    return {
      paddingTop: (styleValues[`${basePath}.paddingTop`] as number) ?? 0,
      paddingRight: (styleValues[`${basePath}.paddingRight`] as number) ?? 0,
      paddingBottom: (styleValues[`${basePath}.paddingBottom`] as number) ?? 0,
      paddingLeft: (styleValues[`${basePath}.paddingLeft`] as number) ?? 0,
    };
  }, [styleValues, fieldConfig.paddingConfig]);

  // Handler for padding changes
  const handlePaddingChange = useCallback(
    (path: string, value: number) => {
      if (onStyleChange) {
        const basePath = fieldConfig.paddingConfig?.basePath;
        const usesEdgeInsets = basePath?.endsWith('.layout.insets') || basePath?.endsWith('.insets');
        const nextPath = usesEdgeInsets
          ? path
              .replace('.paddingTop', '.top')
              .replace('.paddingRight', '.end')
              .replace('.paddingBottom', '.bottom')
              .replace('.paddingLeft', '.start')
          : path;
        onStyleChange(nextPath, value);
      }
    },
    [onStyleChange, fieldConfig.paddingConfig]
  );

  // Get multi-locale upload preference for reset toggle
  const { savedPreference, clearPreference } = useMultiLocaleUpload();

  // Handle checkbox toggle
  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;
      onEnabledChange(event.target.checked);
    },
    [disabled, onEnabledChange]
  );

  // Handle media field change (URL input or file upload)
  const handleMediaChange = useCallback(
    async (newValue: string | File | null) => {
      if (newValue instanceof File) {
        // File selected - upload it
        setUploading(true);
        try {
          const url = await onUpload(newValue);
          onChange(url);
        } catch (error) {
          console.error('Image upload failed:', error);
          // Keep current value on error
        } finally {
          setUploading(false);
        }
      } else {
        // URL string or null
        onChange(newValue);
      }
    },
    [onChange, onUpload]
  );

  // Handle gear icon click
  const handleGearClick = useCallback(() => {
    if (onStylesExpandToggle) {
      onStylesExpandToggle();
    } else {
      setInternalExpanded((prev) => !prev);
    }
  }, [onStylesExpandToggle]);

  // Handle style field value change
  const handleStyleValueChange = useCallback(
    (stylePath: string, newValue: unknown) => {
      if (onStyleChange) {
        onStyleChange(stylePath, newValue);
      }
    },
    [onStyleChange]
  );

  return (
    <Box
      data-testid={dataTestId ?? 'image-upload-element'}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: FIELD_GAP_SMALL,
        mb: FIELD_MARGIN_BOTTOM,
      }}
    >
      {/* Label Row: Checkbox (optional) + Label */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: FIELD_GAP_SMALL }}>
        {!hideCheckbox && (
          <Checkbox
            data-testid={checkboxTestId}
            checked={enabled}
            onChange={handleCheckboxChange}
            disabled={disabled}
            size="small"
            icon={<Box sx={checkboxIconSx} />}
            checkedIcon={<Box sx={checkboxCheckedIconSx}>✓</Box>}
            sx={checkboxSx}
            inputProps={{
              'aria-label': fieldConfig.label,
              'data-testid': checkboxTestId ? `${checkboxTestId}-input` : undefined,
            } as React.InputHTMLAttributes<HTMLInputElement>}
          />
        )}
        {/* Label with optional guide help */}
        {fieldConfig.guideKey && guideService ? (
          <WithGuideHelp
            guideKey={fieldConfig.guideKey}
            guideService={guideService}
            data-testid={`guide-anchor-${fieldConfig.guideKey}`}
            position="inline-end"
            iconSize="small"
          >
            <Tooltip
              title={SHOW_FIELD_PATH_TOOLTIP ? fieldConfig.path : ''}
              placement="top"
              arrow
              disableHoverListener={!SHOW_FIELD_PATH_TOOLTIP}
            >
              <Typography
                variant="body2"
                sx={isVisuallyDisabled ? mainFieldLabelDisabledSx : mainFieldLabelEnabledSx}
                data-testid={fieldConfig.path}
                aria-label={fieldConfig.path}
              >
                {fieldConfig.label}
              </Typography>
            </Tooltip>
          </WithGuideHelp>
        ) : (
          <Tooltip
            title={SHOW_FIELD_PATH_TOOLTIP ? fieldConfig.path : ''}
            placement="top"
            arrow
            disableHoverListener={!SHOW_FIELD_PATH_TOOLTIP}
          >
            <Typography
              variant="body2"
              sx={isVisuallyDisabled ? mainFieldLabelDisabledSx : mainFieldLabelEnabledSx}
              data-testid={fieldConfig.path}
              aria-label={fieldConfig.path}
            >
              {fieldConfig.label}
            </Typography>
          </Tooltip>
        )}
      </Box>

      {/* Input Row: MediaUploadField + Loading + Gear */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: FIELD_GAP }}>
        <Box sx={{ flex: 1, position: 'relative' }}>
          <MediaUploadField
            value={enabled ? value : null}
            onChange={handleMediaChange}
            mediaType="image"
            disabled={disabled || !enabled || uploading}
            placeholder={fieldConfig.placeholder || 'Enter URL or upload image'}
            size="small"
            fullWidth
            showPreview={true}
            inputTestId={inputTestId}
            uploadButtonTestId={uploadButtonTestId}
            previewTestId={previewTestId}
          />
          {/* Loading overlay during upload */}
          {uploading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                borderRadius: 1,
              }}
            >
              <CircularProgress size={24} />
              <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                Uploading...
              </Typography>
            </Box>
          )}
        </Box>

        {/* Gear Icon (only if styleFields or paddingConfig exist) */}
        {hasExpandableContent && (
          <IconButton
            data-testid={stylesToggleTestId ?? 'image-upload-style-toggle'}
            onClick={handleGearClick}
            size="small"
            disabled={disabled}
            aria-label="Toggle style settings"
            sx={isStylesExpanded ? expandedToggleButtonActiveSx : expandedToggleButtonSx}
          >
            <TuneIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Style Settings Panel (Expandable) - 2-column grid layout + PaddingElement */}
      {hasExpandableContent && (
        <Collapse in={isStylesExpanded} timeout="auto" unmountOnExit>
          <Box
            data-testid="image-upload-style-panel"
            sx={{ mt: FIELD_STYLE_MARGIN_TOP, ...expandedSectionSx }}
          >
            {/* Style Fields Grid */}
            {hasStyleFields && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: FIELD_STYLE_GRID_GAP,
                }}
              >
                {fieldConfig.styleFields!.map((styleConfig) => (
                  <StyleFieldRow
                    key={styleConfig.path}
                    config={styleConfig}
                    value={styleValues[styleConfig.path]}
                    onChange={(newValue) => handleStyleValueChange(styleConfig.path, newValue)}
                    disabled={disabled || !enabled}
                    inputTestId={getStyleInputTestId(fieldConfig.label, styleConfig.label)}
                    checkboxTestId={getStyleCheckboxTestId(fieldConfig.label, styleConfig.label)}
                  />
                ))}
              </Box>
            )}

            {/* Padding Element (cross-layout) */}
            {hasPaddingConfig && fieldConfig.paddingConfig && (
              <PaddingElement
                basePath={fieldConfig.paddingConfig.basePath}
                values={paddingValues}
                onChange={handlePaddingChange}
                disabled={disabled || !enabled}
                label={fieldConfig.paddingConfig.label}
              />
            )}

            {/* Reset Multi-Locale Preference Toggle */}
            {savedPreference && (
              <Box
                data-testid="reset-locale-preference"
                sx={{
                  mt: 1,
                  pt: 1,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Auto-apply to {savedPreference === 'all' ? 'all locales' : 'current locale'}
                </Typography>
                <Button
                  size="small"
                  onClick={clearPreference}
                  data-testid="reset-locale-preference-button"
                >
                  Reset
                </Button>
              </Box>
            )}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

export default ImageUploadElement;
